
import Vue from 'vue';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import type { RawLocation } from 'vue-router';
import PublicPortfolioModal from '~/components/partials/public-portfolio/PublicPortfolioModal.vue';
import BravadoTextInputV3 from '~/components/common/BravadoTextInputV3/BravadoTextInputV3.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import BravadoCallout from '~/components/common/BravadoCallout/BravadoCallout.vue';
import { linkedInUrl as validateLinkedInUrl } from '~/components/utils/validate/rules';
import type { FormState } from '~/types/common';
import type { ApproveTestimonialMixinType } from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/mixins/approveTestimonialMixin';
import approveTestimonialMixin from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/mixins/approveTestimonialMixin';

interface IData {
  form: { linkedin: string };
  modalName: string;
  formState: FormState;
}
const DEFAULT_HINT = 'This allows us to verify the authenticity of the testimonial';

export default (Vue as ApproveTestimonialMixinType).extend({
  name: 'PublicPortfolioApproveTestimonialModal',
  components: { BravadoTextInputV3, PublicPortfolioModal, BravadoButtonV3, BravadoCallout },
  mixins: [validationMixin, approveTestimonialMixin],
  data(): IData {
    return {
      form: {
        linkedin: '',
      },
      formState: 'idle',
      modalName: this.$constants.dynamicModals.PUBLIC_PORTFOLIO_APPROVE_TESTIMONIAL_MODAL,
    };
  },
  validations: {
    form: {
      linkedin: {
        required,
        url: validateLinkedInUrl,
      },
    },
  },
  computed: {
    linkedinHint(): string {
      if (this.formState !== 'idle') {
        if (!this.$v.form.linkedin!.required) return 'Linkedin url is required';
        if (!this.$v.form.linkedin!.url) return 'Linkedin url is invalid';

        return DEFAULT_HINT;
      } else {
        return DEFAULT_HINT;
      }
    },
    isValid(): boolean {
      return !(this.$v.form.$dirty && this.$v.form.$invalid);
    },
    isButtonDisabled(): boolean {
      return (this.$v.form.$dirty && this.$v.form.$invalid) || this.formState === 'submitting';
    },
    approveScreenshotRoute(): RawLocation | null {
      return this.actionNeededTestimonial
        ? {
            query: {
              dynamic_modal: this.$constants.dynamicModals.PUBLIC_PORTFOLIO_SCREENSHOT_TESTIMONIAL_MODAL,
              'testimonial-id': this.actionNeededTestimonial.id,
            },
          }
        : null;
    },
  },
  methods: {
    async onTestimonialApprove(): Promise<void> {
      this.$v.form.$touch();
      if (!this.$v.form.$invalid) {
        this.formState = 'submitting';

        this.logEvent('linkedin');
        try {
          await this.approveTestimonial({
            author: { linkedinUrl: this.form.linkedin },
            confirmationScreenshotSignedId: null,
          });
          this.$v.form.$reset();
        } catch {
          this.$notice.error('This testimonial has already been approved');
        } finally {
          this.formState = 'idle';
        }
      }
    },
  },
});
